import intl from "react-intl-universal";
import type { TablePaginationProps } from "../../types";
import {
  PageButton,
  PageButtonGroup,
  PageSizeField,
  PageSizeSelect,
  PageSizeWrapper,
  PaginationBar,
  PaginationInfo,
  PaginationLabel,
} from "./styled";

const DEFAULT_VISIBLE_PAGES = 10;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const formatCount = (value: number) => String(value);

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const buildPageWindow = (
  pageIndex: number,
  totalPages: number,
  visiblePageCount: number
): Array<number> => {
  if (totalPages <= 0) {
    return [];
  }

  const safeVisiblePageCount = Math.max(1, visiblePageCount);
  const windowSize = Math.min(totalPages, safeVisiblePageCount);
  const currentPage = clamp(pageIndex, 0, totalPages - 1);
  let start = Math.max(0, currentPage - Math.floor(windowSize / 2));
  let end = start + windowSize - 1;

  if (end >= totalPages) {
    end = totalPages - 1;
    start = Math.max(0, end - windowSize + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const getRangeText = (pageIndex: number, pageSize: number, totalItems: number) => {
  if (totalItems <= 0 || pageSize <= 0) {
    return `0-0 ${intl.get("commons.table.of")} ${formatCount(0)} ${intl.get("commons.table.records")}`;
  }

  const currentPage = Math.max(0, pageIndex);
  const start = currentPage * pageSize + 1;
  const end = Math.min((currentPage + 1) * pageSize, totalItems);

  return `${formatCount(start)}-${formatCount(end)} ${intl.get("commons.table.of")} ${formatCount(totalItems)} ${intl.get("commons.table.records")}`;
};

const TablePagination = ({
  pageIndex,
  pageSize,
  totalItems,
  onPaginationChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  visiblePageCount = DEFAULT_VISIBLE_PAGES,
}: TablePaginationProps) => {
  const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;

  if (totalPages <= 0) {
    return null;
  }

  const safePageIndex = clamp(pageIndex, 0, totalPages - 1);
  const pageWindow = buildPageWindow(safePageIndex, totalPages, visiblePageCount);
  const rangeText = getRangeText(safePageIndex, pageSize, totalItems);
  const isFirstPage = safePageIndex <= 0;
  const isLastPage = safePageIndex >= totalPages - 1;

  const handlePageChange = (nextPageIndex: number) => {
    if (nextPageIndex === safePageIndex) {
      return;
    }

    onPaginationChange({
      pageIndex: nextPageIndex,
      pageSize,
    });
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    onPaginationChange({
      pageIndex: 0,
      pageSize: nextPageSize,
    });
  };

  return (
    <PaginationBar aria-label={intl.get("commons.table.page")}>
      <PageSizeWrapper>
        <PageSizeField>
          <PageSizeSelect
            aria-label={intl.get("commons.linesPerPage")}
            value={pageSize}
            onChange={(event) => handlePageSizeChange(Number(event.target.value))}
          >
            {Array.from(new Set([pageSize, ...pageSizeOptions]))
              .filter((option) => option > 0)
              .sort((left, right) => left - right)
              .map((option) => (
                <option key={option} value={option}>
                  {formatCount(option)}
                </option>
              ))}
          </PageSizeSelect>
          <PaginationLabel>{intl.get("commons.linesPerPage")}</PaginationLabel>
        </PageSizeField>
      </PageSizeWrapper>

      <PageButtonGroup aria-label={intl.get("commons.table.page")}>
        <PageButton
          type="button"
          onClick={() => handlePageChange(safePageIndex - 1)}
          disabled={isFirstPage}
          aria-label={intl.get("commons.table.previousPage")}
        >
          &lt;
        </PageButton>

        {pageWindow.map((page) => {
          const isCurrentPage = page === safePageIndex;

          return (
            <PageButton
              key={page}
              type="button"
              onClick={() => handlePageChange(page)}
              disabled={isCurrentPage}
              aria-current={isCurrentPage ? "page" : undefined}
              aria-label={`${intl.get("commons.table.page")} ${page + 1}`}
              $active={isCurrentPage}
            >
              {page + 1}
            </PageButton>
          );
        })}

        <PageButton
          type="button"
          onClick={() => handlePageChange(safePageIndex + 1)}
          disabled={isLastPage}
          aria-label={intl.get("commons.table.nextPage")}
        >
          &gt;
        </PageButton>
      </PageButtonGroup>

      <PaginationInfo aria-live="polite">{rangeText}</PaginationInfo>
    </PaginationBar>
  );
};

export default TablePagination;
